import { NPCS, npcDef } from '../content/npcs';
import type { DialogueState } from '../ui/app/store';
import type { CharacterSave } from './character';
import { availableFrom, questDef, turnInsFor } from './quests';

const SERVICE_LABEL: Record<string, string> = {
  shop: 'Browse wares',
  forge: 'Temper equipment',
  bounty: 'Bounty Board',
  market: 'Open the Market',
  arena: 'Enter the Crucible',
  dungeon: 'Instances & raids',
  bonds: 'Songs & bonds',
  haven: 'Manage Haven Isle',
  sail: 'Set sail',
  cards: 'Relic cards',
  sigils: 'Sigils',
  gems: 'Gem cutting',
  storage: 'Storage',
  travel: 'Travel',
};

/** Builds the dialogue tree for talking to an NPC. */
export function npcDialogue(c: CharacterSave, npcId: string, bondStage: number): DialogueState {
  const def = npcDef(npcId);
  const opts: DialogueState['options'] = [];
  const lines: string[] = [];
  const turnIns = turnInsFor(c, npcId);
  const offers = availableFrom(c, npcId);
  if (turnIns[0]) {
    lines.push(...turnIns[0].dialogue.complete);
    for (const q of turnIns) opts.push({ label: `Complete: ${q.name}`, kind: 'turnin', id: q.id });
  } else {
    // An active quest step asks us to talk to this NPC.
    const talk = Object.values(c.quests.active).find((p) => {
      const q = questDef(p.id);
      return q.steps[p.step]?.objectives.some((o) => o.type === 'talk' && o.target === npcId);
    });
    if (talk) {
      const q = questDef(talk.id);
      lines.push(q.dialogue.progress ?? def.greeting);
      opts.push({ label: 'Continue', kind: 'turnin', id: `talk:${q.id}` });
    } else if (offers[0]) {
      lines.push(...offers[0].dialogue.offer);
    } else {
      lines.push(def.greeting);
    }
  }
  for (const q of offers) opts.push({ label: q.name, kind: 'quest', id: q.id });
  for (const s of def.services) opts.push({ label: SERVICE_LABEL[s] ?? s, kind: 'service', id: s });
  if (def.bond) opts.push({ label: bondStage > 0 ? 'Share a moment (Bond)' : 'Get acquainted (Bond)', kind: 'bond', id: npcId });
  opts.push({ label: 'Farewell', kind: 'bye' });
  return { npcId, name: def.name, role: def.role, lines: lines.length ? lines : [def.greeting], index: 0, options: opts };
}

export function npcName(id: string): string {
  return NPCS[id]?.name ?? id;
}
